










































import Vue, { PropType } from 'vue';
import { emailVerificationURL } from '~/utils/urlUtils';
import DialogService from '~/services/DialogService';
import i18n from '~/localization/i18n';
import { SchoolVerification, SchoolVerificationState } from '~/services/types/school';
import SchoolVerificationService from '~/services/SchoolVerificationService';

export default Vue.extend({
    name: 'StudyProgramVerificationInfo',
    props: {
        schoolId: {
            type: Number,
            required: true,
        },
        schoolName: {
            type: String,
            required: true,
        },
        isPresent: {
            type: Boolean,
            required: true,
        },
        verification: {
            type: Object as PropType<SchoolVerification>,
            default: null,
        },
    },
    data() {
        return {
            isLoading: false,
            verificationService: new SchoolVerificationService(),
        };
    },
    computed: {
        safeVerification(): SchoolVerification {
            return this.verification || {};
        },
        isVisible(): boolean {
            return (
                this.needsVerification ||
                this.safeVerification.state === SchoolVerificationState.pending
            );
        },
        needsVerification(): boolean {
            return (
                !this.verification ||
                this.safeVerification.state === SchoolVerificationState.needVerification
            );
        },
        style(): string {
            return this.needsVerification ? 'danger' : 'warning';
        },
        isManualVerification(): boolean {
            return this.safeVerification.studentEmail == null;
        },
        email(): string {
            return (this.safeVerification.studentEmail || '').toLowerCase();
        },
        headerKey(): string {
            return this.needsVerification
                ? this.buildTranslationKey('notYetVerified')
                : this.buildTranslationKey('verificationPending');
        },
        textKey(): string {
            if (this.needsVerification) {
                return this.buildTranslationKey('notYetVerifiedDescription');
            }

            return this.isManualVerification
                ? this.buildTranslationKey('verificationPendingDescription')
                : this.buildTranslationKey('verificationPendingWithEmailDescription');
        },
        primaryActionKey(): string | null {
            if (this.needsVerification) {
                return this.buildTranslationKey('verify');
            }

            return this.isManualVerification ? null : this.buildTranslationKey('resendEmail');
        },
        secondaryActionKey(): string | null {
            if (!this.needsVerification) {
                return this.buildTranslationKey('cancelVerification');
            }

            return this.isPresent ? null : this.buildTranslationKey('not_now');
        },
        primaryAction(): () => void {
            if (this.needsVerification) {
                return this.openVerificationDialog;
            }

            return !this.isManualVerification ? this.resendVerification : () => {};
        },
        secondaryAction(): () => void {
            return this.needsVerification ? this.ignoreVerification : this.cancelVerification;
        },
    },
    methods: {
        buildTranslationKey(key: string): string {
            return `shared.schoolVerification.${key}`;
        },
        openVerificationDialog(): void {
            DialogService.openUniVerifyDialog({
                payload: {
                    currentSchoolId: this.schoolId,
                    schoolSelectionDisabled: true,
                },
                then: () => this.reloadVerifications(),
            });
        },
        resendVerification(): void {
            this.isLoading = true;
            this.$interfaces.university.connection.confirm
                .post({
                    body: {
                        id: this.safeVerification.id,
                        returnUrl: emailVerificationURL(),
                    },
                })
                .then(() => {
                    this.$interfaces.messageModule.addNewMessage({
                        title: i18n.t('components.miscellaneous.verifyModule.emailSent'),
                        message: i18n.t('components.miscellaneous.verifyModule.emailSentMessage'),
                    });
                })
                .catch(() => {
                    this.$interfaces.messageModule.addNewMessage({
                        state: 'ERROR',
                        title: i18n.t('components.miscellaneous.verifyModule.resendErrorTitle'),
                        message: i18n.t('components.miscellaneous.verifyModule.resendErrorMessage'),
                    });
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        changeState(
            newState: SchoolVerificationState,
            successTitleKey: string,
            successMessageKey: string
        ): void {
            this.isLoading = true;
            this.verificationService
                .updateState(this.schoolId, newState)
                .then(() => {
                    this.reloadVerifications();
                    this.$interfaces.messageModule.addNewMessage({
                        title: i18n.t(successTitleKey),
                        message: i18n.t(successMessageKey),
                    });
                })
                .catch(() => {
                    this.$interfaces.messageModule.addNewMessage({
                        state: 'ERROR',
                        title: i18n.t(
                            'components.miscellaneous.verifyModule.statusChangeErrorTitle'
                        ),
                        message: i18n.t(
                            'components.miscellaneous.verifyModule.statusChangeErrorMessage'
                        ),
                    });
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        ignoreVerification(): void {
            if (this.isPresent) {
                return;
            }

            const confirmIgnore = ({ res }: any) => {
                this.changeState(
                    SchoolVerificationState.ignored,
                    'components.miscellaneous.verifyModule.verificationIgnoredTitle',
                    'components.miscellaneous.verifyModule.verificationIgnoredMessage'
                );
                res();
            };

            const cancelIgnore = ({ res }: any) => {
                res();
            };

            this.$interfaces.dialogModal.openModal({
                title: i18n.t(this.buildTranslationKey('ignoreConfirmationTitle')),
                content: i18n.t(this.buildTranslationKey('ignoreConfirmationText')),
                clickawayOff: false,
                actions: [
                    {
                        title: i18n.t('general.cancel'),
                        variant: 'primary',
                        action: cancelIgnore,
                    },
                    {
                        title: i18n.t('general.continue'),
                        variant: 'outline-primary',
                        action: confirmIgnore,
                    },
                ],
            });
        },
        cancelVerification(): void {
            this.changeState(
                SchoolVerificationState.needVerification,
                'components.miscellaneous.verifyModule.verificationCancelledTitle',
                'components.miscellaneous.verifyModule.verificationCancelledMessage'
            );
        },
        reloadVerifications(): void {
            this.$interfaces.university.connection.get({ forceUpdate: true });
        },
    },
});
